import { useState, useEffect, useCallback, useMemo } from 'react';
import { Menu, MenuWithId } from '~/types/store/menu';

import getStores from '~/firebase/get/getStores';
import getMultiStoreMenus from '~/firebase/get/getMultiStoreMenus';
import getOrderCountInPlatform from '~/firebase/get/getOrderCountInPlatfromItems';

import * as fp from 'lodash/fp';
import isInOperatingTime from '~/utils/isInOperatingTime';
import isInPreOrderTime from '~/utils/isInPreOrderTime';
import isCanPreOrderTime from '~/utils/isCanPreOrderTime';
import { UniqItem } from '~/types/store/item';

export default function usePopularItems(
  storesId: Array<string>,
  pickMealAt: Date | null,
  pinnedItems: Array<UniqItem>,
) {
  const [inOperatingStoresId, setInOperatingStoresId] = useState<Array<string>>([]);

  const [menus, setMenus] = useState<Array<MenuWithId>>([]);

  const [availableItemsSet, setAvailableItemsSet] = useState<Set<string>>(new Set());

  const [orderItems, setOrderItems] = useState<Array<UniqItem>>([]);

  const handleSetInOperatingStoresId = useCallback(
    async (storesId: Array<string>, pickMealAt: Date | null) => {
      try {
        const stores = await getStores(storesId);

        const now = new Date();
        const filterStoresId = stores
          .filter(
            ({ preOrderSetting: { schedule, afterHours, beforeHours } }) =>
              pickMealAt && isCanPreOrderTime(schedule, afterHours, beforeHours, pickMealAt, now),
          )
          .map(({ id }) => id);

        setInOperatingStoresId(filterStoresId);
      } catch (err) {
        console.error('get stores doc error', err);
      }
    },
    [setInOperatingStoresId],
  );

  useEffect(() => {
    if (fp.gt(storesId.length, 0)) {
      handleSetInOperatingStoresId(storesId, pickMealAt);
    }
  }, [storesId, pickMealAt]);

  const handleSetMenus = useCallback(
    async (storesId: Array<string>, pickMealAt: Date | null) => {
      try {
        const menus = await getMultiStoreMenus(storesId);
        const filterMenus = menus
          .filter(({ allowPublish }) => allowPublish)
          .filter(({ schedule }) => isInOperatingTime(schedule, pickMealAt));

        setMenus(filterMenus);

        setAvailableItemsSet(new Set(filterMenus.flatMap(({ items }) => items)));
      } catch (err) {
        console.error('get menus error');
      }
    },
    [setMenus, setAvailableItemsSet],
  );

  useEffect(() => {
    if (fp.gt(inOperatingStoresId.length)) {
      handleSetMenus(inOperatingStoresId, pickMealAt);
    }
  }, [inOperatingStoresId, pickMealAt]);

  // 只取取餐當天的庫存，因此取餐時間變更時要重新取得
  useEffect(() => {
    let isCancelled = false;

    getOrderCountInPlatform(pickMealAt)
      .then((items) => {
        if (!isCancelled) setOrderItems(items);
      })
      .catch((err) => {
        console.error('get popular items error', err);
      });

    return () => {
      isCancelled = true;
    };
  }, [pickMealAt]);

  const displayItems = useMemo(() => {
    return fp
      .uniqBy<UniqItem>('uniqId')(pinnedItems.concat(orderItems))
      .filter(({ id }) => availableItemsSet.has(id))
      .filter(({ storeId }) => inOperatingStoresId.includes(storeId));
  }, [availableItemsSet, orderItems, pinnedItems, inOperatingStoresId]);

  return displayItems;
}
